import { Link, useNavigate } from "react-router-dom";
import {
  Container, Row, Card, Breadcrumb, Col
} from "react-bootstrap";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Sort,
  Page as Paging,
  PageSettingsModel,
  Filter,
  Resize,
  FilterSettingsModel,
  TextWrapSettingsModel
} from "@syncfusion/ej2-react-grids";
import {
  Page,
  Spinner,
  IconButton,
  IconButtonType,
  Divider
} from "@whitespace/whitespace-ui";
import { useEffect, useState, useCallback } from "react";
import { useStateMachine } from "little-state-machine";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import AmplifyError from "components/AmplifyError/AmplifyError";
import { makeGridAACompliant } from "modules/SyncfusionAAFixer";
import { parseUsingParseInt } from "helpers/stringUtils";

import "styles/sf-datagrid.scss";
import { updateSelectedHwrcSite } from "actions/updateSelectedHwrcSite";
import IDisposalSiteViewModel from "interfaces/IDisposalSiteViewModel";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";
import EditTemplate from "./Templates/EditTemplate";
import NextNonWorkingPeriodTemplate from "./Templates/NextNonWorkingPeriodTemplate";
import { mapDisposalSiteModelToViewModels } from "./DataMappers/ManageDisposalSiteMapper";
import MapViewTemplate from "./Templates/MapViewTemplate";

const pageSize: number = parseUsingParseInt(process.env.REACT_APP_PAGE_SIZE);
const pageSettings: PageSettingsModel = {
  pageSize,
  pageSizes: [5, 10, 20, 50, "All"]
};
const wrapSettings: TextWrapSettingsModel = {
  wrapMode: "Content"
};
const filterSettings: FilterSettingsModel = { type: "Excel" };
let grid: GridComponent | undefined;

const ManageDisposalSites = () => {
  const [showExceptions, setShowExceptions] = useState<boolean>(false);
  const [renderingComplete, setRenderingComplete] = useState(false);
  const { actions } = useStateMachine({
    updateSelectedHwrcSite
  });
  const {
    isSending, errors, data, statusCode, sendRequest
  } = useAmplifyREST();
  const navigate = useNavigate();

  const [disposalSites, setDisposalSites] = useState<
  IDisposalSiteViewModel[] | undefined
  >(undefined);

  useEffect(() => {
    sendRequest("settings/disposalsites", "GET");
  }, [sendRequest]);

  useEffect(() => {
    if (data && data.disposalSites) {
      setDisposalSites(mapDisposalSiteModelToViewModels(data.disposalSites));
    }
  }, [data, setDisposalSites]);

  useEffect(() => {
    if (errors) setShowExceptions(true);
  }, [errors]);

  useEffect(() => {
    if (![0, 200].includes(statusCode)) {
      setRenderingComplete(true);
    }
  }, [statusCode]);

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  const isProcessing = isSending || !renderingComplete;

  return (
    <Page>
      {showExceptions && errors && (
        <AmplifyError
          errors={errors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}

      <Divider />
      <Card>
        <Card.Header>
          <div className="float-start">
            <h2 data-testid="disposalsites-management">
              Disposal Site Management
              <Spinner
                animation="grow"
                visible={isProcessing}
                spinnerText="Loading"
                variant="info"
              />
            </h2>
          </div>
          <div className="float-end">
            <IconButton
              data-testid="add-disposalsite"
              iconType={IconButtonType.Add}
              variant="outline-primary btn-create"
              onClick={() => navigate(RoutePathsEnum.SettingsDisposalSitesAdd)}
            >
              Add New Disposal Site
            </IconButton>
          </div>
        </Card.Header>
        <Card.Body className="no-padding">
          <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small">
            <Col xs={8} className="d-flex">
              <Breadcrumb
                className="left-margin-large no-wrap"
                data-testid="breadcrumb"
              >
                <Breadcrumb.Item
                  data-testid="breadcrumb-settings"
                  linkAs={Link}
                  linkProps={{ to: RoutePathsEnum.Settings }}
                  onClick={() => navigate(RoutePathsEnum.Settings)}
                >
                  Settings
                </Breadcrumb.Item>
                <Breadcrumb.Item active data-testid="breadcrumb-disposalsites">
                  Disposal Sites
                </Breadcrumb.Item>
              </Breadcrumb>
            </Col>
          </Row>
          <Divider />
          <section className="pt-2">
            <Container fluid>
              <Row>
                <div className="control-pane">
                  <div className="control-section" data-testid="grid">
                    <GridComponent
                      ref={(g: GridComponent) => {
                        grid = g;
                      }}
                      id="disposalSitesGrid"
                      data-testid="disposalSitesGrid"
                      allowPaging
                      allowResizing
                      pageSettings={pageSettings}
                      allowSorting
                      allowFiltering
                      filterSettings={filterSettings}
                      allowSelection={false}
                      dataSource={disposalSites}
                      actionComplete={() => {
                        makeGridAACompliant(grid!, () => {
                          setRenderingComplete(true);
                        });
                      }}
                      loadingIndicator={{ indicatorType: "Shimmer" }}
                      type=""
                      textWrapSettings={wrapSettings}
                      allowTextWrap
                    >
                      <ColumnsDirective>
                        <ColumnDirective
                          field="id"
                          headerText="id"
                          visible={false}
                          isPrimaryKey
                        />
                        <ColumnDirective
                          field="name"
                          headerText="Disposal Site Name"
                          width={70}
                        />
                        <ColumnDirective
                          field="regionsDisplayValue"
                          headerText="Region Name(s)"
                          hideAtMedia="(min-width:800px)"
                          width={100}
                        />
                        <ColumnDirective
                          field="nextNonWorkingPeriodDisplayValue"
                          headerText="Next NWD (Non-working day)"
                          template={NextNonWorkingPeriodTemplate}
                          hideAtMedia="(min-width:800px)"
                          width={100}
                        />
                        <ColumnDirective
                          field="statusDisplayValue"
                          headerText="Status"
                          width={50}
                          hideAtMedia="(min-width:1200px)"
                        />
                        <ColumnDirective
                          headerText=""
                          width={50}
                          allowSorting={false}
                          allowResizing={false}
                          template={MapViewTemplate}
                          allowFiltering={false}
                          hideAtMedia="(min-width:1200px)"
                        />
                        <ColumnDirective
                          headerText=""
                          width={50}
                          allowSorting={false}
                          allowResizing={false}
                          template={EditTemplate}
                          allowFiltering={false}
                        />
                      </ColumnsDirective>
                      <Inject services={[Filter, Paging, Sort, Resize]} />
                    </GridComponent>
                  </div>
                </div>
              </Row>
            </Container>
          </section>
        </Card.Body>
      </Card>
    </Page>
  );
};

export default ManageDisposalSites;
